/* Blanc Space Customily integration: keep fixed PDP actions routed through the required personalizer. */
(() => {
  const proxySelector = "[data-customily-proxy-trigger]";
  const visibleTriggerSelector = "[data-customily-personalize-trigger]";
  const personalizeSelector = 'form[is="product-form"] .customily-personalize-button, form[is="product-form"] #customily-personalize-button';
  const modalCartButtonSelector = "#customily-cart-btn";
  const modalCartButtonLabel = "Add to cart";
  const mobileNextButtonLabel = "Next step";
  const emptyPhotoButtonLabel = "Upload photo";
  const uploadingPhotoButtonLabel = "Uploading…";
  const uploadedPhotoButtonLabel = "Change photo";
  const mobileEditPhotoHint = "Drag to move your photo and double-tap to zoom in. Pinch, rotate or use these controls to fine-tune it.";
  const desktopEditPhotoHint = "Drag to move your photo. Double-click to zoom, or use these controls to fine-tune it.";
  const mobileBreakpoint = window.matchMedia("(max-width: 767px)");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let mobileStepIndex = 0;
  let openWhenReady = false;
  let missingPhotoTimer = 0;
  let orphanSpinnerRecheckTimer = 0;
  const orphanSpinnerSince = new WeakMap();
  let queuedOpenFrame = 0;
  let stableMobileCanvasWidth = 0;
  let canvasLayoutFrame = 0;
  let forceCanvasLayout = false;
  let lastViewportWidth = window.innerWidth;
  let modalPinchActive = false;

  const getPersonalizeButton = () => document.querySelector(personalizeSelector);

  const getOpenPersonalizer = () => {
    const modal = document.querySelector(".customily-modal-container");
    if (!modal?.getClientRects().length) return null;

    const style = window.getComputedStyle(modal);
    return style.display === "none" || style.visibility === "hidden" ? null : modal;
  };

  const stabilizeMobileCanvas = ({ force = false } = {}) => {
    canvasLayoutFrame = 0;
    forceCanvasLayout ||= force;

    if (!mobileBreakpoint.matches) {
      forceCanvasLayout = false;
      stableMobileCanvasWidth = 0;
      return;
    }

    const modal = getOpenPersonalizer();
    const canvas = window.engraver?.canvas;
    const canvasElement = canvas?.upperCanvasEl;
    if (!modal || !canvasElement || !modal.contains(canvasElement) || typeof window.engraver?.setWidth !== "function") {
      forceCanvasLayout = false;
      return;
    }

    const currentWidth = Math.round(Number(canvas.width) || canvasElement.getBoundingClientRect().width || 0);
    if (!currentWidth) {
      forceCanvasLayout = false;
      return;
    }

    /* Customily recalculates the Fabric canvas when its photo controls appear.
       Those controls are visually fixed by the theme, but the vendor measures
       them before that layout settles and can repeatedly shrink the preview.
       Retain the largest valid width from this modal session, while clamping it
       to the real mobile viewport after an orientation/responsive change. */
    const viewportLimit = Math.max(1, Math.floor(window.innerWidth - 28));
    /* If iOS interrupts Customily during a pinch, the vendor can persist both
       the canvas width and DEFAULT_ZOOM at a thumbnail-sized value. Do not use
       that corrupt value as the first session baseline: a mobile modal canvas
       must occupy a meaningful portion of its full-width preview column. */
    const minimumUsableWidth = Math.floor(viewportLimit * 0.82);
    stableMobileCanvasWidth = Math.min(
      Math.max(stableMobileCanvasWidth, currentWidth, minimumUsableWidth),
      viewportLimit,
    );
    const targetWidth = stableMobileCanvasWidth;
    const currentZoom = typeof canvas.getZoom === "function" ? canvas.getZoom() : null;
    const defaultZoom = Number(window.engraver.DEFAULT_ZOOM);
    const hasStaleViewport =
      Number.isFinite(currentZoom) && Number.isFinite(defaultZoom) && Math.abs(currentZoom - defaultZoom) > 0.001;

    if (forceCanvasLayout || Math.abs(currentWidth - targetWidth) > 1 || hasStaleViewport) {
      window.engraver.setWidth(targetWidth);
      window.engraver._setParentSize?.();
    }

    forceCanvasLayout = false;
  };

  const scheduleMobileCanvasStabilization = ({ force = false } = {}) => {
    forceCanvasLayout ||= force;
    if (canvasLayoutFrame) return;

    canvasLayoutFrame = window.requestAnimationFrame(() => {
      canvasLayoutFrame = window.requestAnimationFrame(() => stabilizeMobileCanvas({ force: forceCanvasLayout }));
    });
  };

  const containPersonalizerPinch = (event) => {
    // Cheap checks first: this runs non-passively on every touchstart/touchmove,
    // so ordinary one-finger scrolling must exit before getOpenPersonalizer()
    // forces style/layout work. Containment behaviour is unchanged — it only
    // ever acts on multi-touch pinches and WebKit gesture events.
    const isTouchPinch = "touches" in event && event.touches.length > 1;
    const isWebKitGesture = event.type.startsWith("gesture");
    if (!isTouchPinch && !isWebKitGesture) return;

    if (!mobileBreakpoint.matches || !getOpenPersonalizer()) return;

    modalPinchActive = true;
    event.preventDefault();
  };

  const finishPersonalizerPinch = () => {
    if (!modalPinchActive) return;

    modalPinchActive = false;
    /* A customer crop changes the placeholder object, not the Fabric
       viewport. Reasserting the stable viewport after the gesture preserves
       that crop while recovering from the vendor's occasional tiny-canvas
       transform on iOS. */
    scheduleMobileCanvasStabilization({ force: true });
  };

  const isPersonalizerReady = () => {
    const modal = document.querySelector(".customily-modal-container");
    const options = modal?.querySelectorAll("#cl_optionsapp .customily_option");

    return Boolean(
      getPersonalizeButton() &&
        modal?.querySelector(".canvas-container canvas") &&
        options?.length &&
        [...options].some((option) => option.textContent.trim()),
    );
  };

  const openPersonalizer = () => {
    if (!isPersonalizerReady()) return false;

    getPersonalizeButton().click();
    return true;
  };

  const flushQueuedOpen = () => {
    if (!openWhenReady || queuedOpenFrame || !isPersonalizerReady()) return;

    /* Customily inserts its hidden trigger shortly before the configured modal.
       Let the completed option/canvas DOM receive two layouts before opening so
       a fast customer click cannot expose Customily's off-canvas build state. */
    queuedOpenFrame = window.requestAnimationFrame(() => {
      queuedOpenFrame = window.requestAnimationFrame(() => {
        queuedOpenFrame = 0;

        if (openWhenReady && openPersonalizer()) openWhenReady = false;
      });
    });
  };

  const queuePersonalizerOpen = () => {
    openWhenReady = true;
    syncProxyButtons();
  };

  const positionEditHint = (modal) => {
    const controls = modal?.querySelector(".customily-mobile-controls");
    const hint = modal?.querySelector("[data-bs-customily-edit-hint]");
    if (!controls || !hint) return;

    const bounds = controls.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    hint.style.setProperty("--bs-customily-hint-left", `${bounds.left + bounds.width / 2}px`);
    hint.style.setProperty("--bs-customily-hint-top", `${bounds.top}px`);
  };

  const showEditHint = (option) => {
    const modal = option?.closest(".customily-modal-container");
    if (!modal?.querySelector(".customily-mobile-controls") || modal.hasAttribute("data-bs-customily-edit-hint-shown")) {
      return;
    }

    /* Only show the guidance once per modal session. Set the guard before
       inserting the hint so the modal observer cannot trigger it twice. */
    modal.setAttribute("data-bs-customily-edit-hint-shown", "");

    let hint = modal.querySelector("[data-bs-customily-edit-hint]");
    if (!hint) {
      hint = document.createElement("div");
      hint.dataset.bsCustomilyEditHint = "";
      hint.setAttribute("role", "note");
      modal.append(hint);
    }

    const message = document.createElement("span");
    const closeButton = document.createElement("button");

    message.dataset.bsCustomilyEditHintText = "";
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");
    message.textContent = mobileBreakpoint.matches ? mobileEditPhotoHint : desktopEditPhotoHint;
    closeButton.type = "button";
    closeButton.dataset.bsCustomilyEditHintClose = "";
    closeButton.setAttribute("aria-label", "Dismiss photo editing help");
    closeButton.textContent = "×";
    closeButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      hint.removeAttribute("data-bs-customily-edit-hint-visible");
      hint.setAttribute("aria-hidden", "true");
      option.querySelector(".customily-download-button")?.focus({ preventScroll: true });
    });
    hint.replaceChildren(message, closeButton);
    positionEditHint(modal);
    hint.setAttribute("data-bs-customily-edit-hint-visible", "");
    hint.removeAttribute("aria-hidden");
  };

  const syncUploadButtons = () => {
    document.querySelectorAll(".customily-modal-container .customily-download-button").forEach((button) => {
      const option = button.closest(".customily_option");
      const input = option?.querySelector('input[type="file"]');
      const removeButton = option?.querySelector(".cl_remove-image");
      const loadingSpinners = [...button.querySelectorAll(".spinner-border")];
      const hasUploadedPhoto = Boolean(input?.files?.length);
      let isUploading = button.disabled || loadingSpinners.length > 0;

      /* User testing (Aug 2026): the vendor's spinner span occasionally
         outlives a completed upload by ~5s while the photo already sits on
         the preview, so the shopper waits on a meaningless animation. A
         spinner over a populated input WITHOUT the disabled state that
         persists past a short grace period is treated as orphaned: the
         button reads "Change photo" and CSS hides the stray spinner. The
         disabled state stays authoritative for real in-flight uploads. */
      if (loadingSpinners.length > 0 && !button.disabled && hasUploadedPhoto) {
        const firstSeen = orphanSpinnerSince.get(button) || Date.now();
        orphanSpinnerSince.set(button, firstSeen);
        if (Date.now() - firstSeen > 1500) {
          isUploading = false;
          button.setAttribute("data-bs-customily-spinner-orphaned", "");
        } else {
          window.clearTimeout(orphanSpinnerRecheckTimer);
          orphanSpinnerRecheckTimer = window.setTimeout(syncUploadButtons, 1600);
        }
      } else {
        orphanSpinnerSince.delete(button);
        button.removeAttribute("data-bs-customily-spinner-orphaned");
      }

      // A photo landing clears any "add your photo" rescue warning below.
      if (hasUploadedPhoto) option?.querySelector("[data-bs-customily-missing-photo]")?.remove();

      /* Customily's loading spinner is an empty rotating span. Never put copy
         inside it: rotating text is unreadable and was the source of the
         circular "Change photo" bug. */
      loadingSpinners.forEach((spinner) => {
        if (spinner.textContent.trim()) spinner.replaceChildren();
      });

      button.toggleAttribute("data-bs-customily-photo-uploaded", hasUploadedPhoto);
      button.toggleAttribute("data-bs-customily-photo-uploading", isUploading);
      removeButton?.toggleAttribute("hidden", !hasUploadedPhoto);

      const desiredLabel = isUploading
        ? uploadingPhotoButtonLabel
        : hasUploadedPhoto
          ? uploadedPhotoButtonLabel
          : emptyPhotoButtonLabel;

      const visibleLabel = [...button.querySelectorAll("span")].find(
        (span) => !span.classList.contains("spinner-border") && window.getComputedStyle(span).display !== "none",
      );
      // A photo button mid-rebuild by Customily can have no visible label span;
      // skip this pass rather than throw (label lands on the next sync).
      if (visibleLabel && visibleLabel.textContent.trim() !== desiredLabel) {
        visibleLabel.replaceChildren(document.createTextNode(desiredLabel));
      }
      button.setAttribute("aria-label", desiredLabel);

      if (hasUploadedPhoto && !isUploading) showEditHint(option);
    });
  };

  const syncModalCartButtonLabel = (label = modalCartButtonLabel) => {
    const button = document.querySelector(modalCartButtonSelector);
    if (!button) return;

    const labelNode = [...button.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (labelNode && labelNode.textContent.trim() !== label) {
      labelNode.textContent = label;
    } else if (!labelNode) {
      button.append(document.createTextNode(label));
    }

    button.setAttribute("aria-label", label);
  };

  const getMobileSteps = () =>
    [...document.querySelectorAll(".customily-modal-container #cl_optionsapp .customily_option")].filter(
      (option) => option.textContent.trim(),
    );

  const animateMobileStep = (step, direction) => {
    if (!direction || reducedMotion.matches || typeof step.animate !== "function") return;

    const offset = direction > 0 ? 42 : -42;
    [...step.children].forEach((child) => {
      child.animate(
        [
          { opacity: 0.15, transform: `translateX(${offset}px)` },
          { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: 320, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
    });
  };

  const syncMobileSteps = ({ reset = false, direction = 0 } = {}) => {
    const steps = getMobileSteps();
    if (!steps.length) {
      syncModalCartButtonLabel();
      return;
    }

    if (!mobileBreakpoint.matches) {
      steps.forEach((step) => {
        step.removeAttribute("data-bs-customily-active");
        step.removeAttribute("aria-hidden");
        step.inert = false;
      });
      steps[0].closest(".customily-modal-container")?.removeAttribute("data-bs-customily-final-step");
      syncModalCartButtonLabel();
      return;
    }

    if (reset) mobileStepIndex = 0;
    mobileStepIndex = Math.min(Math.max(mobileStepIndex, 0), steps.length - 1);

    steps.forEach((step, index) => {
      const isActive = index === mobileStepIndex;
      step.toggleAttribute("data-bs-customily-active", isActive);
      step.setAttribute("aria-hidden", String(!isActive));
      step.inert = !isActive;
      if (isActive) animateMobileStep(step, direction);
    });

    const modal = steps[0].closest(".customily-modal-container");
    const previous = modal?.querySelector(".control-buttons:not(.next) .options-control-button");
    const next = modal?.querySelector(".control-buttons.next .options-control-button:not(.options-control-preview-button)");
    const isFinalStep = mobileStepIndex === steps.length - 1;
    if (previous) previous.disabled = mobileStepIndex === 0;
    if (next) next.disabled = isFinalStep;
    modal?.toggleAttribute("data-bs-customily-final-step", isFinalStep);
    syncModalCartButtonLabel(isFinalStep ? modalCartButtonLabel : mobileNextButtonLabel);
  };

  const moveMobileStep = (direction) => {
    const steps = getMobileSteps();
    if (steps.length < 2) return;

    const nextStepIndex = Math.min(Math.max(mobileStepIndex + direction, 0), steps.length - 1);
    if (nextStepIndex === mobileStepIndex) return;

    mobileStepIndex = nextStepIndex;
    syncMobileSteps({ direction });
  };

  /* User testing (Aug 2026): on multi-step mobile products a shopper who
     skipped a REQUIRED step hit Add to cart and nothing visibly happened —
     Customily's rejection lives on a hidden step. Detection trusts the
     vendor's own validation: on a failed add, Customily reveals the failing
     option's `.customily-required-label` ("* Required") and stamps the
     satisfied ones with an inline display:none. Guessing from empty inputs
     was wrong twice over — it false-alarmed on products whose photo slots
     are genuinely optional (Photo Grid), and it missed required text fields
     (Wildflower name). Customily's own handler is never intercepted; this
     only reacts after it visibly went nowhere. */
  const isFlaggedRequired = (option) => {
    const label = option.querySelector(".customily-required-label");
    if (!label) return false;
    const nodes = [label, ...label.querySelectorAll("*")];
    const styledNodes = nodes.filter((node) => node.getAttribute("style"));
    if (!styledNodes.length) return false; // never validated — not a rejection
    return !nodes.some((node) => /display:\s*none/i.test(node.getAttribute("style") || ""));
  };

  const showMissingRequiredWarning = (option) => {
    const isPhotoOption = Boolean(option.querySelector(".customily-download-button"));
    let warning = option.querySelector("[data-bs-customily-missing-photo]");
    if (!warning) {
      warning = document.createElement("p");
      warning.dataset.bsCustomilyMissingPhoto = "";
      warning.setAttribute("role", "alert");
      const uploadButton = option.querySelector(".customily-download-button");
      if (isPhotoOption && uploadButton?.parentElement) uploadButton.parentElement.insertBefore(warning, uploadButton);
      else option.append(warning);
    }
    warning.textContent = isPhotoOption
      ? "Almost there — add your photo to finish this design."
      : "Almost there — fill in this step to finish your design.";
  };

  const scheduleMissingPhotoCheck = () => {
    window.clearTimeout(missingPhotoTimer);
    missingPhotoTimer = window.setTimeout(() => {
      const modal = getOpenPersonalizer();
      if (!modal) return; // the add went through (or the modal closed)
      const cartButton = modal.querySelector(modalCartButtonSelector);
      if (cartButton?.disabled) return; // genuinely processing — leave it be

      const flaggedOption = [...modal.querySelectorAll("#cl_optionsapp .customily_option")].find(isFlaggedRequired);
      if (!flaggedOption) return; // vendor accepted the add — nothing missing

      const steps = getMobileSteps();
      const stepIndex = steps.indexOf(flaggedOption);
      if (mobileBreakpoint.matches && stepIndex >= 0 && stepIndex !== mobileStepIndex) {
        const direction = stepIndex > mobileStepIndex ? 1 : -1;
        mobileStepIndex = stepIndex;
        syncMobileSteps({ direction });
      }
      showMissingRequiredWarning(flaggedOption);
      if (!reducedMotion.matches) flaggedOption.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }, 450);
  };

  // Filling the step in clears the rescue note straight away.
  document.addEventListener("input", (event) => {
    const option = event.target.closest?.(".customily_option");
    option?.querySelector("[data-bs-customily-missing-photo]")?.remove();
  }, true);

  // Capture phase: Customily's own button handler can stop propagation, so a
  // bubble listener never saw the final add click. This runs before the
  // step-navigation capture handler below and self-guards against Next-step
  // clicks with the same isStepNavigation condition.
  document.addEventListener("click", (event) => {
    const cartButton = event.target.closest(`.customily-modal-container ${modalCartButtonSelector}`);
    if (!cartButton) return;
    const steps = getMobileSteps();
    const isStepNavigation = mobileBreakpoint.matches && steps.length >= 2 && mobileStepIndex !== steps.length - 1;
    if (isStepNavigation) return;
    scheduleMissingPhotoCheck();
  }, true);

  const syncProxyButtons = () => {
    const isReady = isPersonalizerReady();

    document.querySelectorAll(visibleTriggerSelector).forEach((button) => {
      button.toggleAttribute("data-customily-personalize-trigger-loading", !isReady);
      // aria-busy/aria-disabled need the literal "true" token — a valueless
      // attribute (toggleAttribute) is ignored by assistive technology.
      if (isReady) {
        button.removeAttribute("aria-busy");
        button.removeAttribute("aria-disabled");
      } else {
        button.setAttribute("aria-busy", "true");
        button.setAttribute("aria-disabled", "true");
      }
    });

    document.querySelectorAll(proxySelector).forEach((button) => {
      if (isReady) {
        button.removeAttribute("aria-busy");
        button.removeAttribute("aria-disabled");
      } else {
        button.setAttribute("aria-busy", "true");
        button.setAttribute("aria-disabled", "true");
      }
    });

    syncUploadButtons();
    syncMobileSteps();
    flushQueuedOpen();
  };

  document.addEventListener("click", (event) => {
    const proxyButton = event.target.closest(proxySelector);
    const visibleTrigger = event.target.closest(visibleTriggerSelector);
    if (!proxyButton && !visibleTrigger) return;

    /* The regular PDP submit button belongs to Customily once the completed
       modal is ready. Before then, capture the click so the product form or
       Customily cannot react to its partially initialised trigger. */
    if (visibleTrigger && isPersonalizerReady()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (openPersonalizer()) return;

    queuePersonalizerOpen();
  }, true);

  document.addEventListener("submit", (event) => {
    if (!event.target.matches('form[is="product-form"]') || !event.target.querySelector(visibleTriggerSelector)) return;
    if (isPersonalizerReady()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    queuePersonalizerOpen();
  }, true);

  document.addEventListener(
    "click",
    (event) => {
      if (!mobileBreakpoint.matches) return;

      const navigation = event.target.closest(".customily-modal-container .control-buttons");
      if (!navigation) return;

      const steps = getMobileSteps();
      if (steps.length < 2) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      moveMobileStep(navigation.classList.contains("next") ? 1 : -1);
    },
    true,
  );

  /* iPhone Chrome and Safari share WebKit. Prevent only the open modal's
     native viewport pinch so a gesture cannot escape to a zoomed storefront;
     do not stop propagation, because Customily's Fabric/Hammer handlers still
     need the gesture to crop and rotate an uploaded image. */
  document.addEventListener("touchstart", containPersonalizerPinch, { capture: true, passive: false });
  document.addEventListener("touchmove", containPersonalizerPinch, { capture: true, passive: false });
  document.addEventListener("touchend", finishPersonalizerPinch, { capture: true, passive: true });
  document.addEventListener("touchcancel", finishPersonalizerPinch, { capture: true, passive: true });
  document.addEventListener("gesturestart", containPersonalizerPinch, { capture: true, passive: false });
  document.addEventListener("gesturechange", containPersonalizerPinch, { capture: true, passive: false });
  document.addEventListener("gestureend", finishPersonalizerPinch, { capture: true, passive: true });

  /* The large mobile CTA doubles as the primary step navigation, but the
     final click is deliberately left to Customily's native cart handler. */
  document.addEventListener(
    "click",
    (event) => {
      if (!mobileBreakpoint.matches) return;

      const cartButton = event.target.closest(`.customily-modal-container ${modalCartButtonSelector}`);
      const steps = getMobileSteps();
      if (!cartButton || steps.length < 2 || mobileStepIndex === steps.length - 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      moveMobileStep(1);
    },
    true,
  );

  window.addEventListener("customily-modal-show", () => {
    const modal = document.querySelector(".customily-modal-container");
    const hint = modal?.querySelector("[data-bs-customily-edit-hint]");

    modal?.removeAttribute("data-bs-customily-edit-hint-shown");
    hint?.removeAttribute("data-bs-customily-edit-hint-visible");
    hint?.setAttribute("aria-hidden", "true");
    modal?.querySelectorAll("[data-bs-customily-missing-photo]").forEach((warning) => warning.remove());
    window.clearTimeout(missingPhotoTimer);
    syncMobileSteps({ reset: true });
    scheduleMobileCanvasStabilization({ force: true });
  });
  window.addEventListener("resize", () => {
    positionEditHint(document.querySelector(".customily-modal-container"));

    if (Math.abs(window.innerWidth - lastViewportWidth) > 1) {
      lastViewportWidth = window.innerWidth;
      stableMobileCanvasWidth = 0;
      scheduleMobileCanvasStabilization({ force: true });
    }
  });
  mobileBreakpoint.addEventListener("change", () => {
    stableMobileCanvasWidth = 0;
    syncMobileSteps({ reset: true });
    scheduleMobileCanvasStabilization({ force: true });
  });

  const observer = new MutationObserver(() => {
    syncProxyButtons();
    scheduleMobileCanvasStabilization();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  syncProxyButtons();
})();
